import {
  IoArrowBackOutline,
  IoBriefcaseOutline,
  IoCallOutline,
  IoDocumentTextOutline,
  IoDownloadOutline,
  IoMailOutline,
  IoOpenOutline,
  IoTrashOutline,
  IoWarningOutline,
} from "react-icons/io5";
import { deleteApplication, updateApplication } from "../../redux/careers";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ErrorBanner } from "../../components/banner";
import Moment from "moment";
import { Section } from "../../components/section";
import { shadow } from "../../assets/styles/parameters";

const STATUS_OPTIONS = [
  "PENDING",
  "REVIEWED",
  "SHORTLISTED",
  "HIRED",
  "REJECTED",
];

export const ApplicationsEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { error } = useSelector((state) => state.careers);

  const rowData = location.state;
  const [currentStatus, setCurrentStatus] = useState(
    rowData?.status || "PENDING",
  );
  const [activeFile, setActiveFile] = useState(null);

  useEffect(() => {
    if (!rowData) {
      navigate("/careers/applications", { replace: true });
    } else if (rowData.media?.length > 0) {
      // FIX: Direct array access based on new schema
      setActiveFile(rowData.media[0]);
    }
  }, [rowData, navigate]);

  if (!rowData) return null;

  // --- ACTIONS ---

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setCurrentStatus(newStatus);

    // WARNING: This dispatch will currently fail because the backend removed the PATCH endpoint
    dispatch(
      updateApplication({
        id: rowData.id,
        values: { ...rowData, status: newStatus },
      }),
    );
  };

  const handleDelete = () => {
    if (
      window.confirm(
        `Are you sure you want to delete the application for ${rowData.candidate?.firstName}?`,
      )
    ) {
      dispatch(deleteApplication(rowData.id))
        .unwrap()
        .then(() => navigate("/careers/applications"));
    }
  };

  return (
    <Section>
      {error && <ErrorBanner text={error} />}

      <TopBar>
        <BackButton onClick={() => navigate(-1)}>
          <IoArrowBackOutline /> Back to Inbox
        </BackButton>

        <ActionBar>
          <StatusWrapper>
            <StatusLabel>Current Status:</StatusLabel>

            {/* The select is temporarily disabled until the backend restores the PATCH route */}
            <StatusSelect
              value={currentStatus}
              onChange={handleStatusChange}
              disabled
              title="Status updates are temporarily disabled by the API"
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status.replace("_", " ")}
                </option>
              ))}
            </StatusSelect>
            <IoWarningOutline
              color="#F39C12"
              title="Backend PATCH endpoint missing"
            />
          </StatusWrapper>

          <DeleteButton onClick={handleDelete} title="Delete Application">
            <IoTrashOutline size={18} />
          </DeleteButton>
        </ActionBar>
      </TopBar>

      <WorkspaceGrid>
        {/* LEFT PANE: Profile Sidebar */}
        <ProfileSidebar delay="0.1s">
          <CandidateHeader>
            <Avatar>
              {rowData.candidate?.firstName?.charAt(0)}
              {rowData.candidate?.lastName?.charAt(0)}
            </Avatar>
            <CandidateName>
              {rowData.candidate?.firstName} {rowData.candidate?.lastName}
            </CandidateName>
            <ApplyDate>
              Applied {Moment(rowData.createdDate).fromNow()}
            </ApplyDate>
          </CandidateHeader>

          <InfoSection>
            <SectionTitle>Contact Details</SectionTitle>
            <InfoItem>
              <IconBox bg="#E6F2FF" color="#3B5BDB">
                <IoMailOutline />
              </IconBox>
              <InfoText>
                <span>Email</span>
                <strong>{rowData.candidate?.email || "Not provided"}</strong>
              </InfoText>
            </InfoItem>
            <InfoItem>
              <IconBox bg="#F4ECF7" color="#9B59B6">
                <IoCallOutline />
              </IconBox>
              <InfoText>
                <span>Phone</span>
                <strong>{rowData.candidate?.phone || "Not provided"}</strong>
              </InfoText>
            </InfoItem>
          </InfoSection>

          <InfoSection>
            <SectionTitle>Job Details</SectionTitle>
            <InfoItem>
              <IconBox bg="#FFF4E6" color="#F39C12">
                <IoBriefcaseOutline />
              </IconBox>
              <InfoText>
                <span>Applied For</span>
                <strong>{rowData.job?.title || "Unknown Job"}</strong>
              </InfoText>
            </InfoItem>
          </InfoSection>
        </ProfileSidebar>

        {/* RIGHT PANE: Integrated Document Viewer */}
        <ViewerPane delay="0.2s">
          <ViewerHeader>
            <SectionTitle>Application Documents</SectionTitle>

            {/* FIX: Use rowData.media array mapping based on new schema */}
            {rowData.media?.length > 1 && (
              <FileTabs>
                {rowData.media.map((file, idx) => (
                  <FileTab
                    key={idx}
                    $active={activeFile?.url === file.url}
                    onClick={() => setActiveFile(file)}
                  >
                    <IoDocumentTextOutline />{" "}
                    {file.filename || `File ${idx + 1}`}
                  </FileTab>
                ))}
              </FileTabs>
            )}
          </ViewerHeader>

          <ViewerContainer>
            {activeFile ? (
              <>
                <ViewerToolbar>
                  {/* FIX: activeFile.fileName is now activeFile.filename */}
                  <FileText>
                    {activeFile.filename || "Candidate Resume"}
                  </FileText>
                  <ViewerActions>
                    <a
                      href={activeFile.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Download"
                    >
                      <IoDownloadOutline size={20} />
                    </a>
                    <a
                      href={activeFile.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open in New Tab"
                    >
                      <IoOpenOutline size={20} />
                    </a>
                  </ViewerActions>
                </ViewerToolbar>

                <StyledIframe src={activeFile.url} title="Document Viewer" />
              </>
            ) : (
              <EmptyViewer>
                <IoDocumentTextOutline size={48} color="#D1D5DB" />
                <p>No documents were attached to this application.</p>
              </EmptyViewer>
            )}
          </ViewerContainer>
        </ViewerPane>
      </WorkspaceGrid>
    </Section>
  );
};

// --- ANIMATIONS & STYLED COMPONENTS ---

const slideUpFade = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;

  @media screen and (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const WorkspaceGrid = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 1.5rem;
  align-items: start;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;
const BackButton = styled.button`
  background: white;
  border: 1px solid #e5e7eb;
  color: #4b5563;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s;
  &:hover {
    background: #f9fafb;
    color: #111;
  }
`;
const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media screen and (max-width: 640px) {
    width: 100%;
    justify-content: space-between;
  }
`;
const DeleteButton = styled.button`
  background: white;
  border: 1px solid #fccfcf;
  color: #dc3545;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &:hover {
    background: #fdedec;
    border-color: #dc3545;
  }
`;
const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 6px 6px 6px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  flex-wrap: wrap;
`;
const StatusLabel = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;

  /* Hide the label text on very small screens — the select is self-explanatory */
  @media screen and (max-width: 480px) {
    display: none;
  }
`;
const StatusSelect = styled.select`
  padding: 8px 12px;
  border-radius: 6px;
  border: none;
  background-color: #f3f4f6;
  font-size: 14px;
  font-weight: 700;
  color: #111;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  outline: none;
  transition: all 0.2s;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  &:focus {
    background-color: #e6f2ff;
    color: #3b5bdb;
  }
`;
const ProfileSidebar = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: ${shadow.light};
  border: 1px solid rgba(0, 0, 0, 0.02);
  opacity: 0;
  animation: ${slideUpFade} 0.5s ease-out forwards;
  animation-delay: ${(props) => props.delay || "0s"};
`;
const CandidateHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f3f4f6;
`;
const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b5bdb, #9b59b6);
  color: white;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px auto;
  box-shadow: 0 4px 12px rgba(59, 91, 219, 0.2);
`;
const CandidateName = styled.h2`
  margin: 0 0 4px 0;
  font-size: 22px;
  color: #111827;
`;
const ApplyDate = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
`;
const InfoSection = styled.div`
  margin-bottom: 24px;
  &:last-child {
    margin-bottom: 0;
  }
`;
const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 15px;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  &:last-child {
    margin-bottom: 0;
  }
`;
const IconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
  background: ${(props) => props.bg};
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;
const InfoText = styled.div`
  display: flex;
  flex-direction: column;
  span {
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 2px;
  }
  strong {
    font-size: 14px;
    color: #111;
    word-break: break-all;
  }
`;
const ViewerPane = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: ${shadow.light};
  border: 1px solid rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  height: 800px;
  opacity: 0;
  animation: ${slideUpFade} 0.5s ease-out forwards;
  animation-delay: ${(props) => props.delay || "0s"};

  @media screen and (max-width: 1024px) {
    height: auto;
    min-height: 500px;
  }

  @media screen and (max-width: 640px) {
    padding: 16px;
    min-height: 400px;
  }
`;
const ViewerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;
const FileTabs = styled.div`
  display: flex;
  gap: 8px;
`;
const FileTab = styled.button`
  background: ${(props) => (props.$active ? "#E6F2FF" : "#F3F4F6")};
  color: ${(props) => (props.$active ? "#3B5BDB" : "#4B5563")};
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: ${(props) => (props.$active ? "#E6F2FF" : "#E5E7EB")};
  }
`;
const ViewerContainer = styled.div`
  flex-grow: 1;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f9fafb;
`;
const ViewerToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
`;
const FileText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111;
`;
const ViewerActions = styled.div`
  display: flex;
  gap: 12px;
  a {
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;
    cursor: pointer;
    &:hover {
      color: #3b5bdb;
    }
  }
`;
const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: #525659;
`;
const EmptyViewer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  p {
    margin-top: 16px;
    font-size: 15px;
  }
`;
